/**
 * Centralized payment error logging.
 * Har type ka error (network, backend, frontend, payload, header) log hota hai
 * taake koi bhi log miss na ho — payment, events booking, membership sab flows.
 * Frontend errors bhi PaymentErrorLog table mein save hote hain via report-client API.
 */

import axiosInstance, { getAPIURL } from "../api/axiosInstance";
import { isAxiosError } from "axios";

export type PaymentFlow =
  | "membership"
  | "event-booking"
  | "investor-checkout"
  | "company-checkout"
  | "add-payment-method"
  | "payment-api";

export type PaymentErrorSource =
  | "network"
  | "backend"
  | "frontend"
  | "payload"
  | "header"
  | "stripe"
  | "unknown";

export interface PaymentErrorLogParams {
  flow: PaymentFlow;
  errorSource: PaymentErrorSource;
  message: string;
  error?: unknown;
  requestConfig?: {
    url?: string;
    method?: string;
    baseURL?: string;
    headers?: Record<string, string>;
    params?: unknown;
  };
  requestPayload?: unknown;
  responseStatus?: number;
  responseData?: unknown;
  /** Optional: sent to API so DB log has user context. If not provided, current login user context is used when set. */
  userId?: string;
  userEmail?: string;
  userName?: string;
  productId?: string;
}

/** Current login user context for client error reports (set from AppContext so N/A fields are filled). */
let currentUserContext: {
  userId?: string;
  userEmail?: string;
  userName?: string;
} = {};

/**
 * Set the current user context so ClientError logs save with user id/email/name instead of N/A.
 * Call this from AppContext when user is loaded (e.g. when user or objectId is set).
 */
export function setPaymentLogUserContext(ctx: {
  userId?: string;
  userEmail?: string;
  userName?: string;
}): void {
  currentUserContext = { ...ctx };
}

const PAYMENT_LOG_PREFIX = "[PaymentError]";

function sanitizeHeaders(headers?: Record<string, unknown>): Record<string, string> {
  if (!headers || typeof headers !== "object") return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === "authorization") {
      out[k] = v && typeof v === "string" ? "[REDACTED]" : String(v);
    } else if (v != null) {
      out[k] = typeof v === "string" ? v : JSON.stringify(v);
    }
  }
  return out;
}

function sanitizePayload(payload: unknown): unknown {
  if (payload == null) return payload;
  if (typeof payload !== "object") return payload;
  const safe: Record<string, unknown> = {};
  const skipKeys = ["cardNumber", "cvc", "cvc2", "card_cvc"];
  for (const [key, value] of Object.entries(payload)) {
    const lower = key.toLowerCase();
    if (skipKeys.some((s) => lower.includes(s))) {
      safe[key] = "[REDACTED]";
    } else {
      safe[key] = value;
    }
  }
  return safe;
}

/**
 * Log payment error with full context. Console + structured object.
 * Also sends to backend so error is saved in PaymentErrorLog table (admin panel).
 * Fire-and-forget: API failure is ignored so UI is never blocked.
 */
export function logPaymentError(params: PaymentErrorLogParams): void {
  const {
    flow,
    errorSource,
    message,
    error,
    requestConfig,
    requestPayload,
    responseStatus,
    responseData,
    userId: paramUserId,
    userEmail: paramUserEmail,
    userName: paramUserName,
    productId,
  } = params;

  const userId = paramUserId ?? currentUserContext.userId;
  const userEmail = paramUserEmail ?? currentUserContext.userEmail;
  const userName = paramUserName ?? currentUserContext.userName;

  const sanitizedHeaders = requestConfig?.headers
    ? sanitizeHeaders(requestConfig.headers as Record<string, unknown>)
    : undefined;
  const sanitizedPayload =
    requestPayload !== undefined ? sanitizePayload(requestPayload) : undefined;

  const logPayload = {
    timestamp: new Date().toISOString(),
    flow,
    errorSource,
    message,
    request: requestConfig
      ? {
          url: requestConfig.url,
          method: requestConfig.method,
          baseURL: requestConfig.baseURL,
          headers: sanitizedHeaders,
          params: requestConfig.params,
          payload: sanitizedPayload,
        }
      : undefined,
    response:
      responseStatus !== undefined || responseData !== undefined
        ? { status: responseStatus, data: responseData }
        : undefined,
    errorSummary:
      error != null
        ? {
            name: error instanceof Error ? error.name : undefined,
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
          }
        : undefined,
  };

  console.error(PAYMENT_LOG_PREFIX, message, logPayload);

  if (error instanceof Error && error.stack) {
    console.error(PAYMENT_LOG_PREFIX, "Stack:", error.stack);
  }

  // Save to DB (PaymentErrorLog) so admin panel shows frontend errors too. Fire-and-forget.
  try {
    const requestDetailsJson =
      logPayload.request || logPayload.response
        ? JSON.stringify({
            request: logPayload.request,
            response: logPayload.response,
            errorSummary: logPayload.errorSummary,
          })
        : undefined;

    axiosInstance
      .post(getAPIURL("/PaymentErrorLog/report-client"), {
        flow,
        errorSource,
        message,
        requestUrl: requestConfig?.url,
        requestMethod: requestConfig?.method,
        responseStatus: responseStatus ?? undefined,
        requestDetailsJson,
        userId: userId || undefined,
        userEmail: userEmail || undefined,
        userName: userName || undefined,
        productId: productId ?? undefined,
      })
      .catch(() => {
        // Ignore: e.g. network down, 401; don't block or spam console
      });
  } catch {
    // Ignore if building payload or axios fails
  }
}

/**
 * Helper: derive error source from axios error.
 */
export function getPaymentErrorSource(err: unknown): PaymentErrorSource {
  if (!err || typeof err !== "object") return "unknown";
  const ax = err as { response?: unknown; request?: unknown; message?: string };
  if (ax.response) return "backend";
  if (ax.request && !ax.response) return "network";
  if (ax.message?.toLowerCase().includes("network") || ax.message?.toLowerCase().includes("timeout"))
    return "network";
  return "unknown";
}

/**
 * Keep user-visible copy concise; strip vendor/dev jargon that should not appear in the UI.
 */
function sanitizeUserFacingMessage(raw: string): string {
  let s = raw.replace(/\s+/g, " ").trim();
  if (!s) return "";

  // Replace developer / vendor terms with neutral wording for end users.
  s = s
    .replace(/\bStripe\b/gi, "payment")
    .replace(/\bPabbly\b/gi, "")
    .replace(/\bExplorer\b/gi, "")
    .replace(/\bwebhook(s)?\b/gi, "")
    .replace(/\bProductId\b/gi, "product")
    .replace(/\bclient secret\b/gi, "payment details")
    .replace(/\bIdempotencyKey\b/gi, "")
    .replace(/\bSuccessUrl\b/gi, "return link")
    .replace(/\bCancelUrl\b/gi, "cancel link")
    .replace(/\bCHECKOUT_SESSION_ID\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,!?])/g, "$1")
    .trim();

  if (!s) return "Something went wrong. Please try again.";
  if (s.length > 180) return `${s.slice(0, 177)}…`;
  return s;
}

function extractFirstValidationMessage(errors: unknown): string | null {
  if (!errors || typeof errors !== "object") return null;
  for (const v of Object.values(errors as Record<string, unknown>)) {
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === "string")
      return sanitizeUserFacingMessage(v[0]);
    if (typeof v === "string") return sanitizeUserFacingMessage(v);
  }
  return null;
}

/**
 * Helper: extract user-facing message from axios error for UI.
 * Production-oriented: no raw stack traces; clear guidance for network, auth, and server faults.
 */
export function getPaymentErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const res = err.response;
    const status = res?.status;
    const data = res?.data as unknown;

    if (data && typeof data === "object" && data !== null) {
      const o = data as Record<string, unknown>;
      if (typeof o.error === "string" && o.error.trim())
        return sanitizeUserFacingMessage(o.error.trim());
      if (typeof o.detail === "string" && o.detail.trim())
        return sanitizeUserFacingMessage(o.detail.trim());
      if (typeof o.message === "string" && o.message.trim())
        return sanitizeUserFacingMessage(o.message.trim());
      if (typeof o.title === "string" && o.title.trim())
        return sanitizeUserFacingMessage(o.title.trim());
      const fromValidation = extractFirstValidationMessage(o.errors);
      if (fromValidation) return fromValidation;
    }
    if (typeof data === "string" && data.trim())
      return sanitizeUserFacingMessage(data.trim());

    if (status === 401)
      return "Your session has expired. Please sign in again.";
    if (status === 403)
      return "You don't have permission to complete this action. Sign in or try another account.";
    if (status === 404)
      return "We couldn't find that resource. Refresh the page or try again.";
    if (status === 429)
      return "Too many attempts. Please wait a moment and try again.";
    if (status !== undefined && status >= 500)
      return "Something went wrong on our side. Please try again in a few minutes.";
    if (status === 400)
      return "We couldn't process this request. Check your details and try again.";
    if (status === 408 || status === 504)
      return "The request timed out. Please try again.";
    if (status != null)
      return "We couldn't complete this request. Please try again.";
  }

  if (isAxiosError(err) && !err.response) {
    if (err.code === "ECONNABORTED")
      return "The request took too long. Please try again.";
    return "We couldn't reach the server. Check your internet connection and try again.";
  }

  if (err instanceof Error) {
    const m = err.message?.trim() || "";
    if (/network error|failed to fetch|load failed|networkerror/i.test(m))
      return "We couldn't reach the server. Check your internet connection and try again.";
    if (m.length > 0 && m.length <= 400 && !/request failed with status code/i.test(m))
      return sanitizeUserFacingMessage(m);
  }

  if (typeof err === "string" && err.trim())
    return sanitizeUserFacingMessage(err.trim());

  return "We couldn't complete this payment. Please try again, or contact support if the problem continues.";
}
